import { useEffect, useCallback } from 'react';
import * as THREE from 'three';
import { slotPositions } from './constants';

const useMeshUpdater = ({
    config,
    scene,
    modelLibrary,
    selectedSlot,
    deviceParts,
    activeClones,
    fadeRegistry,
    buttonColors
}) => {
    //Memoize the center calculation helper
    const getMeshCenter = useCallback((name) => {
        const model = modelLibrary[name];
        if (!model) return new THREE.Vector3(0, 0, 0);
        const box = new THREE.Box3().setFromObject(model);
        const center = new THREE.Vector3();
        box.getCenter(center);
        return center;
    }, [modelLibrary]);

    useEffect(() => {
        // Exit early if the 3D world isn't ready
        if (!scene || Object.keys(modelLibrary).length === 0) return;

        // Reset the registry for animation loop
        fadeRegistry.current = [];

        const bakedQ2Center = getMeshCenter('grey_plug');
        const bakedQ4Center = getMeshCenter('yellow_plug');
        const deltaQ2toQ4 = new THREE.Vector3().subVectors(bakedQ4Center, bakedQ2Center);

        const isBasicTopActive = config.Q9.variant === 'basic_top';
        const isBackHolesActive = config.inner_components.some(i => i.variant === 'back_holes');
        const isInternalMode = selectedSlot === 'inner_components';

        if (deviceParts.top) {
            const topTarget = (!isBasicTopActive && !isInternalMode) ? 1 : 0;
            deviceParts.top.traverse(c => {
                if (c.isMesh) {
                    c.userData.targetOpacity = topTarget;
                    fadeRegistry.current.push(c);
                }
            });
        }
        if (deviceParts.basicTop) {
            const bTopTarget = (isBasicTopActive && !isInternalMode) ? 1 : 0;
            deviceParts.basicTop.traverse(c => {
                if (c.isMesh) {
                    c.userData.targetOpacity = bTopTarget;
                    fadeRegistry.current.push(c);
                }
            });
        }

        const holeTarget = isBackHolesActive ? 0 : 1;
        if (deviceParts.bottomHole) deviceParts.bottomHole.traverse(c => {
            if (c.isMesh) { c.userData.targetOpacity = holeTarget; fadeRegistry.current.push(c); }
        });
        if (deviceParts.topHole) deviceParts.topHole.traverse(c => {
            if (c.isMesh) { c.userData.targetOpacity = holeTarget; fadeRegistry.current.push(c); }
        });
        if (deviceParts.bottom) deviceParts.bottom.traverse(c => {
            if (c.isMesh) fadeRegistry.current.push(c);
        });

        // Dispose old GPU memory
        const cleanupClones = (keys) => {
            keys.forEach(key => {
                if (activeClones.current[key]) {
                    activeClones.current[key].traverse(c => {
                        if (c.isMesh) {
                            c.geometry.dispose();
                            if (c.material.dispose) c.material.dispose();
                        }
                    });
                    scene.remove(activeClones.current[key]);
                    delete activeClones.current[key];
                }
            });
        };

        cleanupClones(['Q1', 'Q2', 'Q3', 'Q4', 'Q5', 'Q6', 'Q7', 'Q8']);
        cleanupClones(Object.keys(activeClones.current).filter(key => key.startsWith('inner_')));

        // Positioning
        ['Q1', 'Q2', 'Q3', 'Q4', 'Q5', 'Q6', 'Q7', 'Q8'].forEach(slotId => {
            const current = config[slotId];
            if (current.variant !== 'none' && modelLibrary[current.variant]) {
                const clone = modelLibrary[current.variant].clone();
                const isYellow = current.variant === 'yellow_plug';

                // Positioning logic
                if (slotId === 'Q1' || slotId === 'Q2') {
                    if (isYellow) clone.position.sub(deltaQ2toQ4);
                } else if (slotId === 'Q3' || slotId === 'Q4') {
                    if (!isYellow) clone.position.add(deltaQ2toQ4);
                }

                if (slotId === 'Q1' || slotId === 'Q3') {
                    clone.rotation.z = Math.PI;
                    const targetX = (slotId === 'Q1') ? bakedQ2Center.x : bakedQ4Center.x;
                    clone.position.x += (targetX * 2);
                    if (slotId === 'Q1' && isYellow) clone.position.x += Math.abs(deltaQ2toQ4.x) * 2.0;
                    if (slotId === 'Q3' && !isYellow) clone.position.x -= Math.abs(deltaQ2toQ4.x) * 2.0;
                } else if (slotId >= 'Q5') {
                    clone.position.y = (slotPositions[slotId].y - slotPositions['Q5'].y);
                }

                const isFaded = (selectedSlot === 'inner_components' &&
                    ['Q3', 'Q4', 'Q5', 'Q6', 'Q7', 'Q8'].includes(slotId)
                );

                clone.traverse(c => {
                    if (c.isMesh) {
                        c.material = c.material.clone();
                        c.material.transparent = true;

                        if (current.variant.includes('push_button') && c.name === 'push_button_4') {
                            // Use stored color OR default to Blue (#3b82f6) if none set
                            const userColor = buttonColors[slotId] || '#ffffffff';
                            c.material = new THREE.MeshPhysicalMaterial({
                                color: userColor,
                                metalness: 0.0,        // Low metalness for plastic/glass
                                roughness: 0.1,       // Very smooth surface
                                transmission: 2.5,     // Light passes through (The "Lens" effect)
                                thickness: 0.1,        // Volume thickness for refraction
                                clearcoat: 1.0,        // Shiny top coat
                                clearcoatRoughness: 0.1,
                                attenuationColor: userColor, // Color of the volume inside
                                attenuationDistance: 1.0,
                                transparent: true,
                                side: THREE.DoubleSide // Ensure back of lens renders
                            });
                        }

                        c.material.opacity = isFaded ? 0 : 1;
                        c.userData.targetOpacity = isFaded ? 0 : 1;
                        c.material.depthWrite = !isFaded;
                        fadeRegistry.current.push(c);
                    }
                });

                scene.add(clone);
                activeClones.current[slotId] = clone;
            }
        });

        // Internal positioning
        config.inner_components.forEach(item => {
            if (modelLibrary[item.variant] && item.variant !== 'back_holes') {
                const clone = modelLibrary[item.variant].clone();
                clone.renderOrder = 10;
                clone.traverse(c => {
                    if (c.isMesh) {
                        c.material = new THREE.MeshStandardMaterial({
                            color: c.material.color,
                            metalness: 0.2,
                            roughness: 0.5,
                            transparent: true
                        });
                        c.userData.targetOpacity = 1;
                        fadeRegistry.current.push(c);
                    }
                });
                scene.add(clone);
                activeClones.current[`inner_${item.variant}`] = clone;
            }
        });

    }, [config, scene, modelLibrary, selectedSlot, deviceParts, activeClones, fadeRegistry, getMeshCenter, buttonColors]);
};

export default useMeshUpdater;